import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppShell from '@/components/feature/AppShell';
import DateNavigator from '@/pages/dashboard/admin/attendance/components/DateNavigator';
import StaffAttendanceStatsBar from '@/pages/dashboard/admin/staff-attendance/components/StaffAttendanceStatsBar';
import StaffAttendanceTable from '@/pages/dashboard/admin/staff-attendance/components/StaffAttendanceTable';
import { staffAttendanceRecords, staffRoles } from '@/mocks/staffAttendance';
import { adminUser, notifications } from '@/mocks/dashboard';
import { getAllStaffCheckIns, CheckInRecord } from '@/hooks/useCheckIn';

export default function StaffAttendancePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const todayStr = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState<string>(todayStr);
  const [localRecords, setLocalRecords] = useState(() => [...staffAttendanceRecords]);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [liveCheckIns, setLiveCheckIns] = useState<Map<string, CheckInRecord>>(new Map());

  // Load real check-in data on mount
  useEffect(() => {
    setLiveCheckIns(getAllStaffCheckIns());
  }, []);

  // Merge live check-in data into local mock records
  const baseRecords = useMemo(() => {
    const dateRecords = localRecords.filter((r) => r.date === selectedDate);

    if (liveCheckIns.size === 0) return dateRecords;

    return dateRecords.map((record) => {
      const live = liveCheckIns.get(record.staffName);
      if (live && live.date === selectedDate) {
        return {
          ...record,
          status: 'present' as const,
          checkIn: live.checkInTime || record.checkIn,
          checkOut: live.checkOutTime || record.checkOut,
        };
      }
      return record;
    });
  }, [localRecords, selectedDate, liveCheckIns]);

  const filteredRecords = useMemo(() => {
    let result = [...baseRecords];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (r) =>
          r.staffName.toLowerCase().includes(q) ||
          r.staffId.toLowerCase().includes(q) ||
          r.role.toLowerCase().includes(q),
      );
    }
    if (roleFilter) {
      result = result.filter((r) => r.role === roleFilter);
    }
    return result;
  }, [baseRecords, searchQuery, roleFilter]);

  const stats = useMemo(() => {
    const present = filteredRecords.filter((r) => r.status === 'present').length;
    const absent = filteredRecords.filter((r) => r.status === 'absent').length;
    const onLeave = filteredRecords.filter((r) => r.status === 'on-leave').length;
    const halfDay = filteredRecords.filter((r) => r.status === 'half-day').length;
    return {
      total: filteredRecords.length,
      present,
      absent,
      onLeave,
      halfDay,
    };
  }, [filteredRecords]);

  const handleToggle = useCallback((recordId: string) => {
    setLocalRecords((prev) =>
      prev.map((r) =>
        r.id === recordId
          ? {
              ...r,
              status:
                r.status === 'present' || r.status === 'half-day'
                  ? ('absent' as const)
                  : ('present' as const),
            }
          : r,
      ),
    );
  }, []);

  const handleRemarkChange = useCallback((recordId: string, remark: string) => {
    setLocalRecords((prev) =>
      prev.map((r) => (r.id === recordId ? { ...r, remark } : r)),
    );
  }, []);

  const handleMarkAll = useCallback(
    (status: 'present' | 'absent') => {
      const targetIds = new Set(baseRecords.map((r) => r.id));
      setLocalRecords((prev) =>
        prev.map((r) =>
          targetIds.has(r.id) && r.status !== 'on-leave' ? { ...r, status } : r,
        ),
      );
    },
    [baseRecords],
  );

  const liveCount = useMemo(() => {
    let count = 0;
    liveCheckIns.forEach((v) => {
      if (v.date === selectedDate) count++;
    });
    return count;
  }, [liveCheckIns, selectedDate]);

  return (
    <AppShell
      pageTitle={t('attendance.staffAttendance')}
      pageSubtitle={t('attendance.staffAttendanceSubtitle')}
      breadcrumbs={[
        { label: t('common.home') },
        { label: t('common.dashboard'), path: '/dashboard/admin' },
        { label: t('attendance.staffAttendance') },
      ]}
      notifications={notifications}
    >
      {/* Date Navigator */}
      <div className="mb-5">
        <DateNavigator
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          viewerName={adminUser.name}
          viewerRole="Admin"
        />
      </div>

      {/* Live check-in indicator */}
      {liveCount > 0 && selectedDate === todayStr && (
        <div className="flex items-center gap-2 mb-4 px-4 py-2.5 bg-semantic-success-bg border border-semantic-success/30 rounded-xl">
          <div className="w-2 h-2 rounded-full bg-semantic-success animate-pulse flex-shrink-0" />
          <p className="text-xs font-label text-foreground-700">
            <span className="font-semibold">{liveCount}</span> staff member
            {liveCount !== 1 ? 's' : ''} checked in today via the dashboard —
            check-in times are now visible in the table below.
          </p>
        </div>
      )}

      {/* Filters Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 mb-4">
        <div className="relative flex-1 w-full sm:max-w-sm">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, ID, or role..."
            className="w-full pl-9 pr-9 py-2.5 sm:py-2 text-sm font-label rounded-lg border border-background-200 bg-background-50 text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-secondary-300 focus:border-secondary-400"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
            >
              <i className="ri-close-circle-line text-sm" />
            </button>
          )}
        </div>

        <div className="relative">
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="appearance-none pl-3 pr-9 py-2 text-sm font-label rounded-lg border border-background-200 bg-background-50 text-foreground-700 focus:outline-none focus:ring-2 focus:ring-secondary-300 focus:border-secondary-400 cursor-pointer"
          >
            <option value="">All Roles</option>
            {staffRoles.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
          <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none" />
        </div>

        <button
          onClick={() => navigate('/dashboard/admin')}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-label font-medium text-foreground-500 hover:text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer ml-auto whitespace-nowrap"
        >
          <i className="ri-arrow-left-line text-sm" />
          {t('common.backToDashboard')}
        </button>
      </div>

      {/* Stats Bar */}
      <StaffAttendanceStatsBar
        totalStaff={stats.total}
        presentCount={stats.present}
        absentCount={stats.absent}
        onLeaveCount={stats.onLeave}
        halfDayCount={stats.halfDay}
      />

      {/* Attendance Table */}
      <StaffAttendanceTable
        records={baseRecords}
        selectedDate={selectedDate}
        onToggle={handleToggle}
        onRemarkChange={handleRemarkChange}
        onMarkAll={handleMarkAll}
        searchQuery={searchQuery}
        roleFilter={roleFilter}
      />
    </AppShell>
  );
}